import { route } from "preact-router";
import { useState } from "preact/hooks";
import { appInfo } from "@sage/shared";
import {
  SignedIn,
  SignedOut,
  ClerkLoading,
  useAuth,
  useUser,
  useClerk,
  useOrganizationList,
  OrganizationSwitcher,
} from "../../auth/clerk";

const links = [
  { href: "/", label: "Home" },
  { href: "/explore", label: "Explore" },
  { href: "/pricing", label: "Pricing" },
  { href: "/docs", label: "Docs" },
] as const;

function navigate(href: string, e: MouseEvent) {
  e.preventDefault();
  route(href);
}

function Brand() {
  return (
    <a class="brand" href="/" aria-label="Sage home" onClick={(e) => navigate("/", e)}>
      <span class="brand-mark" aria-hidden="true">S</span>
      <span>{appInfo.name}</span>
    </a>
  );
}

function AuthActions() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { userMemberships, isLoaded: orgsLoaded } = useOrganizationList();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!isSignedIn) {
    return (
      <div class="nav-actions" aria-label="Authentication actions">
        <a class="ghost-button" href="/login" onClick={(e) => navigate("/login", e)}>
          Log in
        </a>
      </div>
    );
  }

  const hasOrganizations = orgsLoaded && (userMemberships?.data?.length ?? 0) > 0;

  return (
    <div class="nav-actions" aria-label="User actions">
      {/* Org switcher — only shown when the user has at least one organization.
          This prevents Clerk from prompting users to "build an organization"
          before they can access the app. Organizations are optional in Sage. */}
      {hasOrganizations ? (
        <div class="nav-org-switcher">
          <OrganizationSwitcher
            hidePersonal={false}
            afterSelectOrganizationUrl="/"
            afterCreateOrganizationUrl="/"
            createOrganizationMode="modal"
          />
        </div>
      ) : null}

      {/* User dropdown */}
      <div class="nav-user-menu">
        <button
          class="nav-user-trigger"
          aria-expanded={menuOpen}
          aria-haspopup="true"
          onClick={() => setMenuOpen((v) => !v)}
        >
          {user?.imageUrl ? (
            <img
              class="nav-user-avatar"
              src={user.imageUrl}
              alt={user.fullName ?? user.primaryEmailAddress?.emailAddress ?? "User"}
              width={28}
              height={28}
            />
          ) : (
            <span class="nav-user-avatar nav-user-avatar--fallback" aria-hidden="true">
              {((user?.firstName || (user?.primaryEmailAddress?.emailAddress ?? "U"))[0] ?? "U").toUpperCase()}
            </span>
          )}
          <span class="nav-user-name">
            {user?.firstName ?? user?.primaryEmailAddress?.emailAddress ?? "Account"}
          </span>
          <svg class={`nav-user-chevron${menuOpen ? " nav-user-chevron--open" : ""}`}
            width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
            <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        {menuOpen ? (
          <div class="nav-user-dropdown" role="menu">
            <div class="nav-user-dropdown-header">
              <span class="nav-user-dropdown-name">{user?.fullName ?? "Account"}</span>
              <span class="nav-user-dropdown-email">{user?.primaryEmailAddress?.emailAddress ?? ""}</span>
            </div>
            <div class="nav-user-dropdown-divider" />
            <button
              role="menuitem"
              class="nav-user-dropdown-item"
              onClick={() => {
                setMenuOpen(false);
                signOut();
              }}
            >
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function Navbar() {
  return (
    <header class="top-nav" role="banner">
      <Brand />

      <nav class="nav-links" aria-label="Primary navigation">
        {links.map((link) => (
          <a href={link.href} key={link.href} onClick={(e) => navigate(link.href, e)}>
            {link.label}
          </a>
        ))}
      </nav>

      <ClerkLoading>
        {/* Show nothing while Clerk initializes */}
        <div class="nav-actions" aria-label="Authentication loading" aria-busy="true" />
      </ClerkLoading>

      <SignedOut>
        <AuthActions />
      </SignedOut>

      <SignedIn>
        <AuthActions />
      </SignedIn>
    </header>
  );
}
